import { Divider, Flex, Text } from "@chakra-ui/react";
import React, { useContext, useMemo } from "react";

import {
  AiInterviewer,
  useAiInterviewsTestInterviewsQuery,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import { AiInterviewerWizardContext } from "../AiInterviewerWizard/AiInterviewerWizardContext";
import {
  AiInterviewerListInterviewItemType,
  CandidateListItemState,
} from "./AiInterviewerListInterviewItem";
import { AiInterviewerListInterviews } from "./AiInterviewerListInterviews";
import AiInterviewerSection from "./AiInterviewerSection";
import { AiInterviewerSettingsTestInterview } from "./AiInterviewerSettingsTestInterview";

type AiInterviewerTestInterviewsProps = {
  aiInterviewer: Pick<AiInterviewer, "id" | "name">;
};

export const AiInterviewerTestInterviews: React.FC<
  AiInterviewerTestInterviewsProps
> = ({ aiInterviewer }) => {
  const { wizardOn } = useContext(AiInterviewerWizardContext);
  const showIntegritySignals = useFeatureFlag(
    "ai_interviewer:interview_integrity:ui"
  );
  const { data, loading, error } = useAiInterviewsTestInterviewsQuery({
    variables: { aiInterviewerId: aiInterviewer.id },
  });

  const interviews: AiInterviewerListInterviewItemType[] = useMemo(
    () =>
      (data?.aiInterviewerTestInterviews ?? []).map((interview) => {
        // Test interviews are always considered completed for display purposes
        const state: CandidateListItemState = interview.evaluation
          ? CandidateListItemState.Completed
          : CandidateListItemState.SummaryPending;

        const signals = interview.integritySignals;
        const detectedSignals = signals?.filter((s) => s.detected) ?? [];

        return {
          id: interview.id,
          callId: interview.callId,
          callCreatedAt: interview.createdAt,
          candidateName: interview.isSimulation
            ? "Simulated candidate"
            : interview.testUser?.fullName || "Test candidate",
          positionTitle: null,
          evaluation: interview.evaluation,
          evaluationDisabled: interview.evaluationDisabled,
          isIncomplete: false, // NOTE: Not relevant for test interviews
          hasRedactedQuestions: false, // NOTE: Not relevant for test interviews
          state,
          hasScorableQuestions: false, // NOTE: Not relevant for test interviews
          integritySignals:
            detectedSignals.length > 0
              ? { detectedCount: detectedSignals.length }
              : null,
          showIntegritySignals,
        };
      }),
    [data?.aiInterviewerTestInterviews, showIntegritySignals]
  );

  if (wizardOn) {
    return (
      <Flex mt="3" justifyContent="center" w="100%">
        <AiInterviewerSection
          w="492px"
          alignSelf="stretch"
          p={8}
          pt={8}
          borderRadius="16px"
          border="1px solid"
          borderColor="blue.200"
        >
          <AiInterviewerSettingsTestInterview
            aiInterviewer={aiInterviewer}
            wizardOn
          />
          <Divider my="10" w="100%" borderColor="gray.200" />
          <AiInterviewerListInterviews
            interviews={interviews}
            loading={loading}
            error={error}
            emptyMessage={
              <>
                <Text fontWeight="semibold" color="gray.500">
                  No test interviews yet
                </Text>
                <Text color="gray.400" fontSize="sm">
                  Test interviews will show up here after you or a teammate
                  completes one.
                </Text>
              </>
            }
            w="100%"
          />
        </AiInterviewerSection>
      </Flex>
    );
  }

  return (
    <Flex mt="3" direction="column" gap="4" flex="1" minH="0">
      <AiInterviewerSection>
        <AiInterviewerSettingsTestInterview aiInterviewer={aiInterviewer} />
      </AiInterviewerSection>
      <AiInterviewerListInterviews
        interviews={interviews}
        loading={loading}
        error={error}
        emptyMessage="No test interviews to show yet."
        flex="1"
      />
    </Flex>
  );
};
